/**
 * 通知相关的 API hooks
 */
import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { notificationApi, NOTIFICATION_LIST_PAGE_SIZE } from '@/services/api/notifications';
import { orgInvitationsApi } from '@/services/api/org-invitations';
import { useNotificationStore } from '@/stores/notificationStore';
import type { AcceptInvitationDto } from '@/types/org';

/** 通知列表单次拉取条数（与 services/api/notifications 保持一致，供 UI 层使用） */
export { NOTIFICATION_LIST_PAGE_SIZE };

type NotificationListParams = { unreadOnly?: boolean; cursor?: string; limit?: number };

async function loadNotificationSnapshot(params?: NotificationListParams) {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const previousNotifications = useNotificationStore.getState().notifications;
    const [items, countData] = await Promise.all([
      notificationApi.list(params),
      notificationApi.unreadCount(),
    ]);
    // 请求期间收到推送/已读更新时，无法判断计数是否已包含变化，重新取数避免漏算或双计。
    if (useNotificationStore.getState().notifications === previousNotifications) {
      return { items, unreadCount: countData.count, previousNotifications };
    }
  }
  // 持续变化时保留本地状态，走调用方已有的同步失败处理，避免无限重试。
  throw new Error('通知持续变化，本轮快照未应用');
}

/** 首屏/切换筛选时同时读取列表与全局未读数，由调用方确认响应仍有效后应用。 */
export function useNotificationSnapshotLoader() {
  return useCallback((params?: NotificationListParams) => loadNotificationSnapshot(params), []);
}

let notificationResync: Promise<void> | null = null;
let hasPendingNotificationResync = false;

/** 校准铃铛，不重放历史通知的业务副作用；失败不影响已完成的业务操作。 */
export function resyncNotificationModel(): Promise<void> {
  if (notificationResync) {
    hasPendingNotificationResync = true;
    return notificationResync;
  }
  notificationResync = (async () => {
    do {
      hasPendingNotificationResync = false;
      try {
        const snapshot = await loadNotificationSnapshot({ limit: NOTIFICATION_LIST_PAGE_SIZE });
        useNotificationStore
          .getState()
          .init(snapshot.items, snapshot.unreadCount, snapshot.previousNotifications);
        useNotificationStore
          .getState()
          .setHasMore(snapshot.items.length >= NOTIFICATION_LIST_PAGE_SIZE);
      } catch (error) {
        console.warn('[notifications] resync failed', error);
      }
    } while (hasPendingNotificationResync);
  })().finally(() => {
    notificationResync = null;
  });
  return notificationResync;
}

export function useNotificationListLoader() {
  return useCallback((params?: NotificationListParams) => notificationApi.list(params), []);
}

/**
 * 标记通知为已读
 */
export function useMarkNotificationRead() {
  return useMutation({
    mutationFn: (notificationId: string) => notificationApi.markRead(notificationId),
  });
}

/**
 * 标记所有通知为已读
 */
export function useMarkAllNotificationsRead() {
  return useMutation({
    mutationFn: () => notificationApi.markAllRead(),
  });
}

/**
 * 标记通知操作已完成
 */
export function useMarkNotificationActionTaken() {
  return useMutation({
    mutationFn: (notificationId: string) => notificationApi.markActionTaken(notificationId),
  });
}

export function useApproveResourceDeleteNotification() {
  return useMutation({
    mutationFn: (notificationId: string) => notificationApi.approveResourceDelete(notificationId),
  });
}

export function useRejectResourceDeleteNotification() {
  return useMutation({
    mutationFn: (notificationId: string) => notificationApi.rejectResourceDelete(notificationId),
  });
}

/**
 * 拒绝组织邀请
 */
export function useDeclineOrgInvitation() {
  return useMutation({
    mutationFn: (request: AcceptInvitationDto) => orgInvitationsApi.declineInvitation(request),
  });
}
